// buildSavePorts — the adapter that maps PrescriptionDetailsPage's vm into the
// framework-free PrescriptionSaveOrchestrator's ports. This is the ONLY
// place the orchestrator's contract touches Vue/component specifics; the orchestrator
// itself stays pure. Extracted as a factory so the container glue is unit-testable.

import { Prescription } from '@/models/Prescription'
import { Drug } from '@/models/Drug/Drug'
import { DrugSource } from '@/models/Drug/DrugProduct'
import { NotificationOptions } from '@/util/NotificationOptionsPresets'
import { EntityType } from '@/models/EntityType'
import { PrescriptionService } from '@/services/PrescriptionService'
import { DrugService } from '@/services/DrugService'
import { DispenseErrorService } from '@/services/DispenseErrorService'
import { SaveIntent } from './saveIntent'
import { BilledFill, captureFillIdentity, shouldSupersedeBilledFill } from './saveDecisions'
import {
    SavePorts,
    SaveSnapshot,
    SavedPrescription,
    KeepBrandSelection,
    DawOverrideLogPayload,
} from './PrescriptionSaveOrchestrator'

/**
 * The subset of the page instance the ports read/drive. Kept structural (not the whole
 * component type) so the container-glue spec can pass a fakeVm.
 */
export interface SavePortsVm {
    prescription: Prescription
    drug: unknown
    store: { id?: number } | null
    patient: { id?: number; deceased?: boolean }

    // gate inputs
    newMode: boolean
    newRefillMode: boolean
    positiveIdEnabled: boolean
    positiveIdConfigLoaded: boolean
    positiveIdTargetId: number | null
    positiveIdGate: unknown
    verifiedToken: string
    enableEquivalentDrugCheck: boolean
    promptGenericSubstitutionOnBrand: boolean
    /** Set by the entry point: true only for save()/saveAndStay() (the equivalents-gate entry points). */
    runEquivalentsGateOnce: boolean
    isUserPrescriptionAdmin: boolean
    originalFillDate: unknown
    showDeaAlert: boolean
    /** The billed fill the Save-and-New supersede latch is armed on (null = unarmed). */
    supersedeBilledFill: BilledFill | null
    /** Armed after a new billed Rx awaits its claim; disarmed at save-start and on save failure. */
    billedNewRxAwaitingClaims: boolean
    queueItemID?: string

    // save payload inputs
    localDispenseItems: unknown[]

    // transient stash for the drug-category answers submitted with the save
    lastSubmittedDrugCategoryAnswers?: unknown[]

    // methods the ports delegate to (kept on the page)
    collectChangedQuestionaries(): unknown[]
    rebaselineSavedQuestionaries(sent: unknown[], failedCategoryIds: number[]): void
    assignViewValues(): void
    getErrorValidation(): boolean
    /** Convert the form to a new-Rx draft; returns a restore that puts it back exactly. */
    prepareNewRxFromBilled(): () => void
    getDrugDetails(drug: unknown): void
    /** Re-fetch the dispense-error alert list so it reflects the true post-save state. */
    refreshDispenseErrors(): void
    addLockWithURL(url: string, ttl: number): Promise<unknown>
    showLockedMessage(entityType: number, id: string, label: string, data: unknown): void
    runPostSaveContinuation(intent: SaveIntent, saved: SavedPrescription): Promise<void>
    $notification(options: unknown): void
    $bvModal: { msgBoxConfirm(message: string): Promise<boolean>; msgBoxOk(message: string, opts?: unknown): Promise<unknown> }

    // modal refs / state the ports drive
    equivalentPrescriptionsData: unknown[]
    showEquivalentModal: boolean
    brandToGenericPromptModal: { show(equivPackageId: number, productName: string | null, candidates: Drug.EquivalentDrug[]): void }
    dawCode: number
}

export function buildSavePorts(vm: SavePortsVm): SavePorts {
    const snapshot = (): SaveSnapshot => {
        const rx = vm.prescription
        const drug = vm.drug as Drug | null

        // Drug-category answers submitted with this save; stash so applySaved can re-baseline.
        const drugCategoryAnswers = vm.collectChangedQuestionaries()
        vm.lastSubmittedDrugCategoryAnswers = drugCategoryAnswers

        const brandPackageId = rx.packageID ?? null
        const drugDrugId = drug ? drug.drugId : undefined
        const equivPackageId = Number(brandPackageId ?? drugDrugId)
        const productId = drug ? Number(drug.productID) : NaN

        const genericPreflightApplies =
            vm.promptGenericSubstitutionOnBrand &&
            drug instanceof Drug &&
            drug.source === DrugSource.GsddFederal &&
            !!drug.productID &&
            !!(rx.packageID || drug.drugId)

        return {
            isNewOrRefill: vm.newMode || vm.newRefillMode,
            patientDeceased: !!vm.patient?.deceased,
            positiveIdEnabled: vm.positiveIdEnabled,
            positiveIdConfigLoaded: vm.positiveIdConfigLoaded,
            positiveIdTargetKnown: vm.positiveIdTargetId !== null,
            positiveIdGatePresent: !!vm.positiveIdGate,
            heldPositiveIdToken: vm.verifiedToken || null,
            equivalentsCheckApplies:
                vm.runEquivalentsGateOnce && vm.enableEquivalentDrugCheck && !!rx.packageID && !!rx.patientID,
            genericPreflightApplies,

            equivalentsQuery: {
                patientId: rx.patientID,
                packageId: rx.packageID,
                drugSource: rx.drugSource,
                storeId: rx.storeID,
                rxNumber: rx.rxNumber,
            },

            productId,
            equivPackageId,
            brandPackageId,
            genericStoreId: rx.storeID ?? vm.store?.id,
            productNameLong: drug?.productNameLong ?? null,
            isExistingFill: !vm.newMode && !vm.newRefillMode && rx.hasFullId,
            rxId: rx.rxID,

            needsIngestionLock: !!vm.queueItemID && !rx.rxNumber,
            ingestionQueueItemId: vm.queueItemID ?? null,
            fillDateTooOld: computeFillDateTooOld(vm),
            showDeaAlert: vm.showDeaAlert,
            // Supersede only when the form no longer identifies the billed fill (drug or
            // written date changed) — unchanged identity saves as a normal update. The latch
            // guard also keeps the identity capture off the every-save path.
            supersedeThisRx: !!vm.supersedeBilledFill
                && shouldSupersedeBilledFill(vm.supersedeBilledFill, rx.rxID, captureFillIdentity(rx)),
            prescription: rx,
            savePayloadExtras: {
                dispensingItems: vm.localDispenseItems,
                drugCategoryAnswers,
                createNewRefill: vm.newRefillMode,
            },
        }
    }

    return {
        api: {
            save: (prescription, extras) => PrescriptionService.save(prescription, extras as any) as Promise<SavedPrescription>,
            getEquivalentPrescriptions: (query) => PrescriptionService.getEquivalentPrescriptions(query),
            getEquivalents: (productId, packageId, storeId) => DrugService.getEquivalents(productId, packageId, storeId),
            dawOverrideExists: (rxId, brandPackageId) => PrescriptionService.dawOverrideLogExists(rxId, brandPackageId),
            postDawOverrideLog: (savedRx: Prescription, payload: DawOverrideLogPayload) => {
                if (savedRx.storeID == null || savedRx.rxNumber == null) {
                    console.warn('Cannot post DAW override log — saved prescription is missing identifiers.', savedRx)
                    return
                }
                const rxId = Prescription.createRxId(savedRx.storeID, savedRx.rxNumber, savedRx.rfNumber)
                void PrescriptionService.postDawOverrideLog(rxId, payload).catch((err) => {
                    console.error('Failed to post DAW override audit log.', { err, response: err?.response })
                    vm.$notification(NotificationOptions.error(
                        'Prescription saved, but the brand-kept audit log failed to record. Please notify support.'))
                })
            },
            resolveDispenseError: (dispenseErrorId: number, rxId: string) => {
                // Fire-and-forget from the orchestrator's post-save success path. This lands after
                // the save resolves, by which point a supersede/finish save may have swapped
                // vm.prescription onto a new Rx — so refresh the alert list only while the page is
                // still on the Rx the resolve was requested against (rxId), never GETting
                // By-RxId for the wrong Rx. On failure the Rx is still saved (that's the atomic
                // win) and the error simply stays open, so we tell the operator to retry from the
                // alert rather than silently swallow it.
                const refreshIfStillOnRx = () => {
                    if (vm.prescription.rxID === rxId) vm.refreshDispenseErrors()
                }
                void DispenseErrorService.resolve(dispenseErrorId)
                    .then(() => refreshIfStillOnRx())
                    .catch((err) => {
                        console.error('Failed to resolve dispense error after save.', { err, response: err?.response })
                        refreshIfStillOnRx()
                        vm.$notification(NotificationOptions.error(
                            'Prescription saved, but the dispense error could not be marked resolved. Please retry from the alert.'))
                    })
            },
        },
        locks: {
            acquireIngestionLock: (queueItemId) => vm.addLockWithURL(`/IngestionQueue/${queueItemId}/lock`, 60000) as Promise<void>,
        },
        ui: {
            notifyDeceasedBlocked: () =>
                vm.$notification(NotificationOptions.error('Cannot save prescriptions for deceased patients.')),
            notifyPositiveIdNotReady: (reason) =>
                vm.$notification(NotificationOptions.warning(reason === 'config-loading'
                    ? 'Loading positive-ID configuration. Please retry in a moment.'
                    : 'Patient is still loading. Please retry once the page finishes loading.')),
            alertFillDateTooOld: () => { void vm.$bvModal.msgBoxOk('You cannot change the fill date to older than 14 days ago.') },
            confirmDea: async () => {
                try {
                    return await vm.$bvModal.msgBoxConfirm(
                        'The prescriber has no DEA number assigned and this prescription is for a controlled substance. Continue saving?') as boolean
                } catch {
                    // Fail open, matching the pre-orchestrator behavior: a rejected confirm
                    // (e.g. the modal torn down by a parallel UI action) must not silently
                    // abort the whole attempt and drop a keep-brand payload / pending intent.
                    return true
                }
            },
            showEquivalentsPrompt: (equivalents) => {
                vm.equivalentPrescriptionsData = equivalents
                vm.showEquivalentModal = true
            },
            showGenericPrompt: (equivPackageId, productName, candidates) =>
                vm.brandToGenericPromptModal.show(equivPackageId, productName, candidates),
            requestPositiveIdVerification: () => (vm.positiveIdGate as { requestVerification(): void }).requestVerification(),
            showIngestionLocked: (error: any) => {
                if (error?.response && error.response.status === 418) {
                    vm.showLockedMessage(EntityType.IngestionQueueItem, String(vm.queueItemID), String(vm.queueItemID), error.response.data)
                } else {
                    vm.$notification(NotificationOptions.error(error))
                }
            },
            notifySaveError: (error: any) => {
                // A failed save must not leave the supersede flag armed for the next attempt.
                vm.billedNewRxAwaitingClaims = false
                if (error?.response?.status === 409 && error.response?.data?.error === 'DUPLICATE_IMAGE_PRESCRIPTION') {
                    void vm.$bvModal.msgBoxOk(
                        'A prescription with this patient, drug, and written date already exists from this image. To create a separate prescription, change the drug or written date; otherwise open the existing prescription.',
                        { title: 'Duplicate Prescription', okVariant: 'warning' })
                    return
                }
                vm.$notification(NotificationOptions.error(error))
            },
        },
        form: {
            snapshot,
            prepareForSave: () => {
                // Disarm any stale supersede flag at the start of every commit: if a prior
                // Save-and-Bill armed it but the cashout failed (no claims landed), a plain
                // save must clear it so the next claimsLoaded can't consume the stale arm and
                // turn a routine save into an identity-wiping new-Rx insert.
                vm.billedNewRxAwaitingClaims = false
                // Save-time prep, ported verbatim from the pre-orchestrator savePrescription: mirror the
                // drug's long name, default quantity/dispensed, then propagate the view-model values
                // (status, DAW, delivery, dates, ...) onto the prescription via assignViewValues.
                if (vm.drug instanceof Drug) {
                    vm.prescription.productNameShort = (vm.drug as Drug).productNameLong
                }
                vm.prescription.quantity = vm.prescription.quantity || 0
                vm.prescription.dispensed = vm.prescription.dispensed || 0
                vm.assignViewValues()
            },
            validate: () => vm.getErrorValidation(),
            consumePositiveIdToken: () => { vm.verifiedToken = '' },
            applyGenericSwap: (generic) => applyGenericSwap(vm, generic),
            applyKeepBrandDaw: (payload: KeepBrandSelection) => {
                vm.dawCode = payload.dawCode
                vm.prescription.dawCode = payload.dawCode
            },
            applySaved: (saved) => {
                vm.prescription = Object.assign(new Prescription(), saved)
                vm.$notification(NotificationOptions.successSaveNotificationPreset(`Prescription ${vm.prescription.rxID}`))
                vm.rebaselineSavedQuestionaries(
                    vm.lastSubmittedDrugCategoryAnswers ?? [],
                    saved.answerSaveFailedCategoryIDs ?? [])
                if (saved.answerSaveWarning) {
                    vm.$notification(NotificationOptions.warning(saved.answerSaveWarning, 'Drug Category Answers'))
                }
            },
            applyNewRxDraft: () => {
                // The page method owns the form snapshot/undo; this port only swaps the latch —
                // disarmed for the new draft, re-armed by the restore so a retry supersedes again.
                const armed = vm.supersedeBilledFill
                vm.supersedeBilledFill = null
                const restore = vm.prepareNewRxFromBilled()
                return () => {
                    restore()
                    vm.supersedeBilledFill = armed
                }
            },
        },
        nav: {
            completeSave: (intent, saved) => vm.runPostSaveContinuation(intent, saved),
        },
        now: () => new Date(),
    }
}

function computeFillDateTooOld(vm: SavePortsVm): boolean {
    const rx = vm.prescription
    if (rx.rxID === '--') return false
    const days = Math.ceil((new Date().getTime() - new Date(rx.fillDate).getTime()) / (1000 * 60 * 60 * 24))
    return !vm.isUserPrescriptionAdmin && days > 15 && rx.fillDate !== vm.originalFillDate
}

// Swap the prescription onto the chosen generic — mirrors the manual drug-selection path.
function applyGenericSwap(vm: SavePortsVm, generic: Drug.EquivalentDrug): void {
    if (!generic.packageID) {
        vm.$notification(NotificationOptions.error('Selected generic is missing a package id.'))
        return
    }
    vm.prescription.packageID = generic.packageID
    vm.prescription.drugSource = DrugSource.GsddFederal
    vm.dawCode = 0
    vm.prescription.dawCode = 0

    const swapped = new Drug(generic.packageID, {
        drugId: generic.packageID,
        packageID: generic.packageID,
        source: DrugSource.GsddFederal,
        productID: generic.productID != null ? String(generic.productID) : '',
        productNameLong: generic.productNameLong ?? '',
        brandGenericStatus: generic.brandGenericStatus ?? 'Generic',
        marketer: generic.marketer ?? '',
    } as any)
    vm.drug = swapped
    vm.getDrugDetails(swapped)
}
